import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Plus, Trash2, ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UOM_OPTIONS } from "@/constants/uom";
import CostHealthBar from "@/components/CostHealthBar";
import SearchableSelect from "@/components/SearchableSelect";
import JobDeliveryAddress from "@/components/JobDeliveryAddress";
import VendorByEmailDialog from "@/components/VendorByEmailDialog";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const emptyItem = (defaults) => ({
  description: "", qty: 1, uom: "each", unit_price: "", cost_code_id: "",
  billability: defaults.billability, markup_percent: defaults.markup, taxable: false,
});

export default function PurchaseRequestForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const [jobs, setJobs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [addressHint, setAddressHint] = useState(null);
  const [addressFixed, setAddressFixed] = useState(false);
  const [contexts, setContexts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [lastDecisionComment, setLastDecisionComment] = useState(null);

  const [contextType, setContextType] = useState("job");
  const [jobId, setJobId] = useState("");
  const [coJobId, setCoJobId] = useState("");
  const [changeOrders, setChangeOrders] = useState([]);
  const [changeOrderId, setChangeOrderId] = useState("");
  const [nonJobContextId, setNonJobContextId] = useState("");
  const [payWithRamp, setPayWithRamp] = useState(false);

  const [title, setTitle] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [deliveryAddressId, setDeliveryAddressId] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [vendorNotes, setVendorNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [items, setItems] = useState([emptyItem({ billability: "non_billable", markup: 0 })]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!isEdit);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [budgetPanel, setBudgetPanel] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const navigate = useNavigate();
  const [showByEmail, setShowByEmail] = useState(false);
  const loadVendors = () => api.get("/contractor/vendor-network").then((r) => setVendors(r.data));

  useEffect(() => {
    api.get("/contractor/jobs").then((r) => setJobs(r.data));
    loadVendors();
    api.get("/contractor/cost-codes").then((r) => setCostCodes(r.data));
    api.get("/contractor/contexts").then((r) => setContexts(r.data));
    api.get("/contractor/settings").then((r) => setSettings(r.data));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/contractor/purchase-requests/${id}`).then((r) => {
      const pr = r.data;
      if (pr.status !== "draft") {
        toast.error("Only a draft purchase request can be edited");
        navigate(`/contractor/purchase-requests/${id}`);
        return;
      }
      const ctx = pr.context || {};
      setContextType(ctx.type || "job");
      if (ctx.type === "job") setJobId(ctx.job_id || "");
      if (ctx.type === "change_order") { setCoJobId(ctx.job_id || ""); setChangeOrderId(ctx.change_order_id || ""); }
      if (ctx.type === "non_job") setNonJobContextId(ctx.context_id || "");
      setTitle(pr.title || "");
      setVendorId(pr.vendor_id || "");
      setPayWithRamp(pr.payment_method === "ramp");
      setDeliveryAddressId(pr.delivery_address_id || "");
      setExpectedDate(pr.expected_delivery_date ? pr.expected_delivery_date.slice(0, 10) : "");
      setVendorNotes(pr.vendor_notes || "");
      setInternalNotes(pr.internal_notes || "");
      setLastDecisionComment(pr.last_decision_comment || null);
      setItems(
        pr.items && pr.items.length > 0
          ? pr.items.map((it) => ({
              description: it.description || "", qty: it.qty ?? 1, uom: it.uom || "each", unit_price: it.unit_price ?? 0,
              cost_code_id: it.cost_code_id || "", billability: it.billability || "non_billable",
              markup_percent: it.markup_percent ?? 0, taxable: !!it.taxable,
            }))
          : [emptyItem({ billability: "non_billable", markup: 0 })]
      );
      setLoaded(true);
    }).catch(() => {
      toast.error("Could not load purchase request");
      navigate("/contractor/purchase-orders");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  useEffect(() => {
    if (contextType !== "change_order" || !coJobId) { setChangeOrders([]); return; }
    api.get(`/contractor/jobs/${coJobId}/change-orders`).then((r) => setChangeOrders(r.data));
  }, [contextType, coJobId]);

  // Delivery goes to the job site or the main office — nothing else, no
  // typed-in addresses (Test Drive 2026-10-21).
  useEffect(() => {
    const effectiveJobId = contextType === "job" ? jobId : contextType === "change_order" ? coJobId : null;
    api.get("/contractor/po-delivery-addresses", { params: { job_id: effectiveJobId || undefined } }).then((r) => {
      setAddresses(r.data.addresses);
      setAddressHint(r.data.hint);
      setAddressFixed(!!r.data.fixed);
      // A QuickBooks job with its address set always delivers there.
      if (r.data.fixed) setDeliveryAddressId(r.data.preselect_id);
      else if (!deliveryAddressId && r.data.preselect_id) setDeliveryAddressId(r.data.preselect_id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextType, jobId, coJobId]);

  const lineDefaults = () => ({
    billability: settings?.default_billability || "non_billable",
    markup: settings?.default_markup_percent || 0,
  });

  const updateItem = (i, field, value) => {
    const next = [...items];
    // unit_price: "" is a real state (TBD price — the vendor fills it in
    // via quote or invoice, Package 35), so never coerce it to 0.
    next[i] = { ...next[i], [field]: field === "qty" || field === "markup_percent" ? Number(value) : field === "unit_price" ? (value === "" ? "" : Number(value)) : value };
    setItems(next);
  };

  const addItem = () => setItems([...items, emptyItem(lineDefaults())]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  // No tax at ordering time — the vendor adds tax per line on their invoice.
  // Taxable lines are taxed at the job's manual tax rate (Test Drive 2026-10-21).
  const taxJobId = contextType === "job" ? jobId : contextType === "change_order" ? coJobId : null;
  const jobTaxRate = jobs.find((j) => j.id === taxJobId)?.tax_rate || 0;
  const lineTotal = (i) => (Number(i.qty) || 0) * (Number(i.unit_price) || 0);
  const subtotal = items.reduce((s, i) => s + lineTotal(i), 0);
  const taxTotal = items.reduce((s, i) => s + (i.taxable ? Math.round(lineTotal(i) * jobTaxRate) / 100 : 0), 0);
  const grandTotal = subtotal + taxTotal;
  const anyTaxable = items.some((i) => i.taxable);

  const buildContext = () => {
    if (contextType === "job") return { type: "job", job_id: jobId };
    if (contextType === "change_order") return { type: "change_order", change_order_id: changeOrderId };
    return { type: "non_job", context_id: nonJobContextId };
  };

  const selectContextType = (t) => {
    setContextType(t);
    setJobId(""); setCoJobId(""); setChangeOrderId(""); setNonJobContextId("");
  };

  const contextReady =
    (contextType === "job" && !!jobId) ||
    (contextType === "change_order" && !!changeOrderId) ||
    (contextType === "non_job" && !!nonJobContextId);

  // Live budget-impact preview — charter requirement: show the impact
  // BEFORE submit, not after. Debounced so it doesn't fire on every
  // keystroke while a line item is being edited.
  useEffect(() => {
    if (!contextReady) { setBudgetPanel(null); return; }
    const handle = setTimeout(() => {
      setBudgetLoading(true);
      const previewItems = items
        .filter((it) => it.description || Number(it.unit_price) > 0)
        .map((it) => ({
          description: it.description || "(item)", qty: Number(it.qty) || 0, uom: it.uom,
          unit_price: it.unit_price === "" || it.unit_price === null ? null : Number(it.unit_price), cost_code_id: it.cost_code_id || null,
          billability: it.billability, markup_percent: it.markup_percent || null, taxable: !!it.taxable,
        }));
      api.post("/contractor/purchase-requests/preview-impact", { context: buildContext(), items: previewItems })
        .then((r) => setBudgetPanel(r.data))
        .catch(() => setBudgetPanel(null))
        .finally(() => setBudgetLoading(false));
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextReady, contextType, jobId, changeOrderId, nonJobContextId, JSON.stringify(items)]);

  const submit = async (submitForApproval) => {
    setFieldErrors([]);
    const nonBlankIndices = [];
    const itemsToSend = [];
    items.forEach((it, idx) => {
      if (!it.description) return;
      nonBlankIndices.push(idx);
      itemsToSend.push({
        description: it.description, qty: Number(it.qty), uom: it.uom,
        unit_price: it.unit_price === "" || it.unit_price === null ? null : Number(it.unit_price),
        cost_code_id: it.cost_code_id || null, billability: it.billability,
        markup_percent: it.markup_percent || null, taxable: !!it.taxable,
      });
    });
    setSaving(true);
    const payload = {
      context: buildContext(), title, vendor_id: vendorId || null, items: itemsToSend,
      delivery_address_id: deliveryAddressId || null, expected_delivery_date: expectedDate || null,
      vendor_notes: vendorNotes || null, internal_notes: internalNotes || null, submit: submitForApproval,
      payment_method: payWithRamp ? "ramp" : null,
    };
    try {
      const { data } = isEdit
        ? await api.patch(`/contractor/purchase-requests/${id}`, payload)
        : await api.post("/contractor/purchase-requests", payload);
      toast.success(submitForApproval ? "Purchase request submitted for approval" : "Draft saved");
      navigate(`/contractor/purchase-requests/${data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (Array.isArray(detail)) {
        const mapped = detail.map((err) => {
          const m = err.field?.match(/^items\[(\d+)\]/);
          if (m) {
            const originalIdx = nonBlankIndices[Number(m[1])];
            return { ...err, lineIndex: originalIdx };
          }
          return err;
        });
        setFieldErrors(mapped);
        toast.error("Please fix the highlighted fields");
      } else {
        toast.error(detail || "Failed to create request");
      }
    } finally {
      setSaving(false);
    }
  };

  const lineError = (i) => fieldErrors.find((e) => e.lineIndex === i)?.message;
  const generalErrors = fieldErrors.filter((e) => e.lineIndex === undefined);

  if (!loaded) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid="new-pr-page">
      {isEdit
        ? <Link to={`/contractor/purchase-requests/${id}`} data-testid="back-link" className="text-[12.5px] text-slate-500 flex items-center gap-1 mb-3"><ChevronLeft size={13} /> Back to Purchase Order</Link>
        : <Link to="/contractor/purchase-orders" data-testid="back-link" className="text-[12.5px] text-slate-500 flex items-center gap-1 mb-3"><ChevronLeft size={13} /> Back to Purchase Orders</Link>}
      <h1 className="text-2xl font-extrabold tracking-tight">{isEdit ? "Edit Purchase Order" : "New Purchase Order"}</h1>
      <p className="text-slate-500 text-sm mt-1">Describe what you need and route it for approval.</p>

      {lastDecisionComment && (
        <div className="mt-4 text-[12.5px] text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3.5 py-3" data-testid="pr-form-last-decision-comment">
          <div className="font-semibold text-[11px] uppercase tracking-wide text-amber-600 mb-0.5">Changes Requested</div>
          {lastDecisionComment}
        </div>
      )}

      {generalErrors.length > 0 && (
        <div className="mt-4 text-[12.5px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 space-y-1" data-testid="pr-general-errors">
          {generalErrors.map((e, i) => <div key={i}>{e.message}</div>)}
        </div>
      )}

      <div className="mt-6">
        <label className="text-[13px] font-semibold text-slate-700">Purchasing Context *</label>
        <div className="mt-1.5 flex gap-2">
          {[["job", "Job"], ["change_order", "Change Order"], ["non_job", "Non-Job"]].map(([t, label]) => (
            <button
              key={t} type="button" onClick={() => selectContextType(t)} data-testid={`context-type-${t}`}
              className={`px-3.5 py-2 rounded-lg text-[12.5px] font-semibold ${contextType === t ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {contextType === "job" && (
          <SearchableSelect value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="pr-job-select" className="mt-2.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400">
            <option value="">Select job…</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.code} — {j.name}</option>)}
          </SearchableSelect>
        )}
        {contextType === "change_order" && (
          <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <SearchableSelect value={coJobId} onChange={(e) => { setCoJobId(e.target.value); setChangeOrderId(""); }} data-testid="pr-co-job-select" className="h-10 px-3 rounded-lg border border-slate-200 text-sm">
              <option value="">Select job…</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.code} — {j.name}</option>)}
            </SearchableSelect>
            <SearchableSelect value={changeOrderId} onChange={(e) => setChangeOrderId(e.target.value)} disabled={!coJobId} data-testid="pr-change-order-select" className="h-10 px-3 rounded-lg border border-slate-200 text-sm disabled:opacity-50">
              <option value="">Select change order…</option>
              {changeOrders.map((co) => <option key={co.id} value={co.id}>{co.code} — {co.name}</option>)}
            </SearchableSelect>
          </div>
        )}
        {contextType === "non_job" && (
          <SearchableSelect value={nonJobContextId} onChange={(e) => setNonJobContextId(e.target.value)} data-testid="pr-context-select" className="mt-2.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
            <option value="">Select context…</option>
            {contexts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </SearchableSelect>
        )}

        {budgetPanel && (
          <div className="mt-3 border border-slate-200 rounded-xl p-4" data-testid="pr-budget-panel">
            <div className="flex items-center justify-between">
              <h3 className="text-[12.5px] font-semibold text-slate-700">Budget Impact</h3>
              {budgetLoading && <span className="text-[11px] text-slate-400">Updating…</span>}
            </div>
            <div className="mt-2.5 grid grid-cols-3 gap-3 text-[12.5px]">
              <div>
                <div className="text-slate-400">Committed</div>
                <div className="font-semibold mono" data-testid="budget-committed">{fmt(budgetPanel.context_costs?.committed)}</div>
              </div>
              <div>
                <div className="text-slate-400">Actual</div>
                <div className="font-semibold mono" data-testid="budget-actual">{fmt(budgetPanel.context_costs?.actual)}</div>
              </div>
              <div>
                <div className="text-slate-400">Pending</div>
                <div className="font-semibold mono" data-testid="budget-pending">{fmt(budgetPanel.context_costs?.pending)}</div>
              </div>
            </div>

            {budgetPanel.context_costs?.budget_total != null ? (
              <div className="mt-3.5">
                <div className="flex justify-between text-[11.5px] text-slate-500 mb-1">
                  <span>Budget {fmt(budgetPanel.context_costs.budget_total)}</span>
                  <span data-testid="budget-remaining-after">
                    {budgetPanel.remaining_after >= 0 ? `${fmt(budgetPanel.remaining_after)} remaining` : `${fmt(Math.abs(budgetPanel.remaining_after))} over`}
                  </span>
                </div>
                <CostHealthBar spent={(budgetPanel.committed_after || 0) + (budgetPanel.context_costs?.actual || 0)} budgetTotal={budgetPanel.context_costs.budget_total} health={budgetPanel.health_after} testId="pr-budget-health-bar" />
                {budgetPanel.over_budget && (
                  <div className="mt-2.5 text-[12px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="over-budget-warning">
                    This is more than what's left in the job's budget. It can be saved as a draft, but not submitted until the job's budget is raised.
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-2 text-[11.5px] text-slate-400">No budget set for this context.</div>
            )}
          </div>
        )}
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-[13px] font-medium text-slate-700">Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="pr-title-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400" placeholder="e.g. Framing materials" />
        </div>
        <div>
          <label className="text-[13px] font-medium text-slate-700">Preferred Vendor</label>
          <SearchableSelect value={vendorId} onChange={(e) => (e.target.value === "__email__" ? setShowByEmail(true) : setVendorId(e.target.value))} data-testid="pr-vendor-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400">
            <option value="">None / decide later</option>
            <option value="__email__">+ Someone not on ScapeSource — send by email…</option>
            <optgroup label="Hosted catalog">
              {vendors.filter((v) => v.has_catalog).map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}
            </optgroup>
            <optgroup label="Listed only (no catalog — blank prices OK)">
              {vendors.filter((v) => !v.has_catalog).map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}
            </optgroup>
          </SearchableSelect>
          <VendorByEmailDialog open={showByEmail} onOpenChange={setShowByEmail} onCreated={async (v) => { await loadVendors(); setVendorId(v.vendor_id); }} />
          <label className="flex items-center gap-2 mt-2 text-[12.5px] text-slate-700">
            <input type="checkbox" checked={payWithRamp} onChange={(e) => setPayWithRamp(e.target.checked)} data-testid="pr-pay-with-ramp" />
            Pay this order with Ramp
          </label>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className="text-[13px] font-medium text-slate-700">Delivery Address *</label>
            <Link to="/contractor/delivery-addresses" className="text-[11.5px] text-sky-600 font-semibold">Manage addresses</Link>
          </div>
          {addressFixed ? (
            <JobDeliveryAddress address={addresses[0]} jobId={contextType === "job" ? jobId : coJobId} testId="pr-job-delivery-address" />
          ) : (
          <SearchableSelect value={deliveryAddressId} onChange={(e) => setDeliveryAddressId(e.target.value)} data-testid="pr-delivery-address-select" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400">
            <option value="">Select address…</option>
            {addresses.map((a) => <option key={a.id} value={a.id}>{a.label}{a.is_default ? " (default)" : ""}</option>)}
          </SearchableSelect>
          )}
          {addressHint && <p className="text-[11.5px] text-slate-400 mt-1" data-testid="delivery-address-fallback-hint">{addressHint}</p>}
        </div>
        <div>
          <label className="text-[13px] font-medium text-slate-700">Expected Delivery Date</label>
          <input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} data-testid="pr-expected-date-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400" />
        </div>
      </div>

      <div className="mt-7">
        <div className="flex items-center justify-between">
          <label className="text-[13px] font-semibold text-slate-700">Line Items</label>
          <button onClick={addItem} data-testid="add-item-button" className="text-[12.5px] font-semibold text-sky-600 flex items-center gap-1 h-11 sm:h-auto px-2 -mr-2"><Plus size={13} /> Add item</button>
        </div>
        <div className="mt-2 space-y-3">
          {items.map((item, i) => (
            <div key={i} className={`border rounded-xl p-3 space-y-2 ${lineError(i) ? "border-red-300 bg-red-50/40" : "border-slate-200"}`} data-testid={`pr-item-row-${i}`}>
              {/* Below sm: one field per row, 44px touch targets — the 12-col grid below is unusable on a phone. */}
              <div className="sm:hidden space-y-2">
                <div className="flex items-center gap-2">
                  <input className="flex-1 h-11 px-3 rounded-lg border border-slate-200 text-[14px] outline-none focus:border-sky-400" placeholder="Description" value={item.description} onChange={(e) => updateItem(i, "description", e.target.value)} data-testid={`item-desc-mobile-${i}`} />
                  <button onClick={() => removeItem(i)} data-testid={`remove-item-mobile-${i}`} className="w-11 h-11 shrink-0 text-slate-400 hover:text-red-500 flex items-center justify-center"><Trash2 size={16} /></button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <input type="number" min="0" className="h-11 px-2.5 rounded-lg border border-slate-200 text-[14px] outline-none focus:border-sky-400" placeholder="Qty" value={item.qty} onChange={(e) => updateItem(i, "qty", e.target.value)} data-testid={`item-qty-mobile-${i}`} />
                  <SearchableSelect className="h-11 px-1.5 rounded-lg border border-slate-200 text-[13px]" value={item.uom} onChange={(e) => updateItem(i, "uom", e.target.value)} data-testid={`item-uom-mobile-${i}`}>
                    {UOM_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </SearchableSelect>
                  <input type="number" min="0" step="0.01" className="h-11 px-2.5 rounded-lg border border-slate-200 text-[14px] outline-none focus:border-sky-400" placeholder="Price" value={item.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} data-testid={`item-price-mobile-${i}`} />
                </div>
                <SearchableSelect className={`w-full h-11 px-2.5 rounded-lg border text-[13px] ${lineError(i) ? "border-red-300" : "border-slate-200"}`} value={item.cost_code_id} onChange={(e) => updateItem(i, "cost_code_id", e.target.value)} data-testid={`item-cost-code-mobile-${i}`}>
                  <option value="">Cost code…</option>
                  {costCodes.map((cc) => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
                </SearchableSelect>
                <div className="flex items-center gap-3">
                  <SearchableSelect className="flex-1 h-11 px-2.5 rounded-lg border border-slate-200 text-[13px]" value={item.billability} onChange={(e) => updateItem(i, "billability", e.target.value)} data-testid={`item-billability-mobile-${i}`}>
                    <option value="non_billable">Non-billable</option>
                    <option value="billable">Billable</option>
                  </SearchableSelect>
                  <label className="flex items-center gap-1.5 text-[13px] text-slate-700 shrink-0">
                    <input type="checkbox" checked={!!item.taxable} onChange={(e) => updateItem(i, "taxable", e.target.checked)} data-testid={`item-taxable-mobile-${i}`} />
                    Taxable
                  </label>
                </div>
              </div>

              <div className="hidden sm:block space-y-2">
                <div className="grid grid-cols-12 gap-2">
                  <input className="col-span-5 h-9 px-2.5 rounded-lg border border-slate-200 text-[13px] outline-none focus:border-sky-400" placeholder="Description" value={item.description} onChange={(e) => updateItem(i, "description", e.target.value)} data-testid={`item-desc-${i}`} />
                  <input type="number" min="0" className="col-span-2 h-9 px-2.5 rounded-lg border border-slate-200 text-[13px] outline-none focus:border-sky-400" placeholder="Qty" value={item.qty} onChange={(e) => updateItem(i, "qty", e.target.value)} data-testid={`item-qty-${i}`} />
                  <SearchableSelect className="col-span-2 h-9 px-2 rounded-lg border border-slate-200 text-[12.5px]" value={item.uom} onChange={(e) => updateItem(i, "uom", e.target.value)} data-testid={`item-uom-${i}`}>
                    {UOM_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </SearchableSelect>
                  <input type="number" min="0" step="0.01" className="col-span-2 h-9 px-2.5 rounded-lg border border-slate-200 text-[13px] outline-none focus:border-sky-400" placeholder="Unit price" value={item.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} data-testid={`item-price-${i}`} />
                  <button onClick={() => removeItem(i)} data-testid={`remove-item-${i}`} className="col-span-1 text-slate-400 hover:text-red-500 flex items-center justify-center"><Trash2 size={15} /></button>
                </div>
                <div className="grid grid-cols-12 gap-2 items-center">
                  <SearchableSelect className={`col-span-5 h-9 px-2 rounded-lg border text-[12.5px] ${lineError(i) ? "border-red-300" : "border-slate-200"}`} value={item.cost_code_id} onChange={(e) => updateItem(i, "cost_code_id", e.target.value)} data-testid={`item-cost-code-${i}`}>
                    <option value="">Cost code…</option>
                    {costCodes.map((cc) => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
                  </SearchableSelect>
                  <SearchableSelect className="col-span-5 h-9 px-2 rounded-lg border border-slate-200 text-[12.5px]" value={item.billability} onChange={(e) => updateItem(i, "billability", e.target.value)} data-testid={`item-billability-${i}`}>
                    <option value="non_billable">Non-billable</option>
                    <option value="billable">Billable</option>
                  </SearchableSelect>
                  <label className="col-span-2 flex items-center gap-1.5 text-[12.5px] text-slate-700">
                    <input type="checkbox" checked={!!item.taxable} onChange={(e) => updateItem(i, "taxable", e.target.checked)} data-testid={`item-taxable-${i}`} />
                    Taxable
                  </label>
                </div>
              </div>
              {lineError(i) && <div className="text-[11.5px] text-red-600" data-testid={`item-error-${i}`}>{lineError(i)}</div>}
              <div className="text-right text-[12px] text-slate-500">Line total: {item.unit_price === "" || item.unit_price === null ? <span className="font-semibold text-amber-600">TBD — vendor to price</span> : fmt((Number(item.qty) || 0) * (Number(item.unit_price) || 0))}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <div className="w-56 space-y-1 text-[13px]">
            <div className="flex justify-between text-slate-500"><span>Subtotal</span><span className="mono" data-testid="pr-subtotal">{fmt(subtotal)}</span></div>
            {anyTaxable && <div className="flex justify-between text-slate-500"><span>Tax{jobTaxRate ? ` (${jobTaxRate}%)` : ""}</span><span className="mono" data-testid="pr-tax-total">{fmt(taxTotal)}</span></div>}
            <div className="flex justify-between font-bold text-[14px] pt-1 border-t border-slate-200"><span>Total</span><span className="mono" data-testid="pr-total">{fmt(grandTotal)}</span></div>
            <div className="text-[11px] text-slate-400 text-right" data-testid="pr-tax-note">
              {!anyTaxable ? "No taxable lines." : taxJobId && !jobTaxRate ? "This job has no tax rate yet — an admin sets it on the job page." : !taxJobId ? "Tax uses the job's rate — pick a job." : "Taxable lines at the job's tax rate."}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-[13px] font-medium text-slate-700">Vendor Notes</label>
          <p className="text-[11.5px] text-slate-400 mb-1">Visible to the vendor on the purchase order.</p>
          <textarea value={vendorNotes} onChange={(e) => setVendorNotes(e.target.value)} data-testid="pr-vendor-notes-input" rows={3} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400" />
        </div>
        <div>
          <label className="text-[13px] font-medium text-slate-700">Internal Notes</label>
          <p className="text-[11.5px] text-slate-400 mb-1">Never shown to the vendor.</p>
          <textarea value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} data-testid="pr-internal-notes-input" rows={3} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400" />
        </div>
      </div>

      <div className="mt-7 flex flex-col sm:flex-row gap-3">
        <button disabled={saving} onClick={() => submit(false)} data-testid="save-draft-button" className="h-11 sm:h-auto px-4 sm:py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50 disabled:opacity-60">Save as Draft</button>
        <button disabled={saving} onClick={() => submit(true)} data-testid="submit-for-approval-button" className="h-11 sm:h-auto px-4 sm:py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold disabled:opacity-60">Submit for Approval</button>
      </div>
    </div>
  );
}
